import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from '@shared/ui';
import { useGetCalendarChangeQuery } from './api/adminCalendarChangesApi';

interface AdminCalendarChangeDetailProps {
  changeId: string;
}

const dateTime = (value?: string) => (value ? new Date(value).toLocaleString() : 'Unknown');

const convergenceLatency = (detectedAt: string, convergedAt?: string) => {
  if (!convergedAt) return 'Not converged';
  const seconds = Math.max(
    0,
    Math.round((new Date(convergedAt).getTime() - new Date(detectedAt).getTime()) / 1000)
  );
  return `${seconds} seconds`;
};

function EvidenceField({ label, value }: { label: string; value: string | number }) {
  return (
    <Box>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
    </Box>
  );
}

export function AdminCalendarChangeDetail({ changeId }: AdminCalendarChangeDetailProps) {
  const { data, error, isLoading } = useGetCalendarChangeQuery(changeId);

  if (isLoading) return <CircularProgress size={24} />;
  if (error || !data)
    return <Alert severity="error">Could not load calendar change details.</Alert>;

  const { change } = data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography component="h1" variant="h5">
          Calendar change
        </Typography>
        <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {change.id}
        </Typography>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Typography component="h2" variant="h6" gutterBottom>
            Operation
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <EvidenceField label="Coach" value={change.coach_person_id} />
            <EvidenceField label="Event" value={change.event_id} />
            <EvidenceField label="Scope" value={change.scope} />
            <EvidenceField label="State" value={change.state} />
            <EvidenceField label="Reason" value={change.reason || 'None'} />
            <EvidenceField label="Detected" value={dateTime(change.detected_at)} />
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography component="h2" variant="h6" gutterBottom>
            Schedule evidence
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <EvidenceField
              label="Prior schedule"
              value={`${dateTime(change.prior_start_at)} - ${dateTime(change.prior_end_at)}`}
            />
            <EvidenceField
              label="Resulting schedule"
              value={`${dateTime(change.resulting_start_at)} - ${dateTime(change.resulting_end_at)}`}
            />
            <EvidenceField label="Provider updated" value={dateTime(change.provider_updated_at)} />
            <EvidenceField
              label="Detection to convergence"
              value={convergenceLatency(change.detected_at, change.converged_at)}
            />
            <EvidenceField
              label="Lead time"
              value={
                change.lead_time_seconds === undefined
                  ? 'Unknown'
                  : `${change.lead_time_seconds} seconds`
              }
            />
            <EvidenceField label="Timezone" value={change.timezone || 'Unknown'} />
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography component="h2" variant="h6" gutterBottom>
            Affected records and policy
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {change.affected_records.map((record) => (
              <Box key={`${record.owner_model}:${record.owner_record_id}`}>
                <Typography>{record.owner_model}</Typography>
                <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {record.owner_record_id}
                </Typography>
              </Box>
            ))}
            <Divider />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(change.policy_flags ?? []).map((flag) => (
                <Chip key={flag} label={flag} size="small" />
              ))}
              {(change.policy_flags?.length ?? 0) === 0 && (
                <Typography color="text.secondary">No policy flags.</Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {change.resolved_at && (
        <Card variant="outlined">
          <CardContent>
            <Typography component="h2" variant="h6" gutterBottom>
              Resolution
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              <EvidenceField label="Resolved" value={dateTime(change.resolved_at)} />
              <EvidenceField label="Actor" value={change.resolved_by || 'Unknown'} />
              <EvidenceField label="Kind" value={change.resolution_kind || 'Unknown'} />
              <EvidenceField label="Note" value={change.resolution_note || 'None'} />
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
